import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../ThemeContext";

const WEBSITE_URL = "https://www.infiniumglobalresearch.com";
const TERMS_URL = `${WEBSITE_URL}/terms-condition`;
const PRIVACY_URL = `${WEBSITE_URL}/privacy-policy`;

// Shared form styles for the Login / Signup cards
export const authLabelClass =
  "block mb-[7px] text-[13px] leading-[1.3] font-semibold text-gray-700 dark:text-gray-300";

export const authInputClass =
  "block w-full h-[47px] px-3.5 rounded-xl border border-gray-300 bg-white text-[15px] text-gray-900 placeholder-gray-400 outline-none transition-all focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 disabled:opacity-60 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder-gray-500 dark:focus:bg-gray-800/80";

export const authInputErrorClass = "!border-red-500 focus:!border-red-500 focus:!ring-red-500/20";

export const authButtonClass =
  "w-full min-h-[49px] px-[18px] py-3 rounded-xl text-[15px] font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-500 shadow-lg transition-all duration-300 enabled:hover:from-cyan-600 enabled:hover:to-blue-600 enabled:hover:shadow-cyan-500/30 enabled:active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-none disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none dark:focus-visible:ring-offset-gray-900 dark:disabled:bg-gray-800 dark:disabled:text-gray-500";

export const authLinkClass =
  "font-semibold text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300 transition-colors";

export const AuthFieldError = ({ children }) => (
  <p className="mt-1 ml-1 text-xs text-red-500">{children}</p>
);

export const AuthAlert = ({ message }) => {
  if (!message) return null;
  const isSuccess = /success|created/i.test(message);
  return (
    <div
      className={`mb-4 p-3 rounded-lg border text-sm ${isSuccess
        ? "bg-green-500/10 border-green-500/20 text-green-600 dark:text-green-400"
        : "bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400"
        }`}
    >
      {message}
    </div>
  );
};

export const EyeIcon = ({ open }) =>
  open ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
    </svg>
  );

export const authEyeButtonClass =
  "absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors";

export const AuthSecurityNote = ({ children }) => (
  <div className="mt-[18px] grid grid-cols-[15px_minmax(0,1fr)] gap-[9px] text-gray-500 dark:text-gray-400">
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="w-[15px] h-[17px] mt-0.5 fill-none stroke-current"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="10" width="14" height="11" rx="2"></rect>
      <path d="M8 10V7a4 4 0 0 1 8 0v3"></path>
    </svg>
    <p className="m-0 text-xs leading-[1.55]">{children}</p>
  </div>
);

export const AuthOtpModal = ({
  title,
  email,
  error,
  otp,
  setOtp,
  onSubmit,
  loading,
  submitLabel,
  loadingLabel,
  onResend,
  onCancel,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
    <div className="w-full max-w-md p-[30px] rounded-2xl border border-gray-200 bg-white shadow-2xl dark:bg-gray-900 dark:border-gray-800">
      <h3 className="m-0 mb-[5px] text-[27px] leading-[1.2] tracking-[-1px] font-extrabold text-gray-900 dark:text-white">
        {title}
      </h3>
      <p className="m-0 mb-5 text-[15px] leading-normal text-gray-600 dark:text-gray-400">
        We've sent a 6-digit code to {email}
      </p>

      <AuthAlert message={error} />

      <form onSubmit={onSubmit} className="grid gap-4">
        <div>
          <label className={authLabelClass}>Enter OTP</label>
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            className={`${authInputClass} !h-14 text-center !text-2xl tracking-[0.4em]`}
            required
            autoFocus
          />
        </div>

        <button type="submit" disabled={otp.length !== 6 || loading} className={authButtonClass}>
          {loading ? loadingLabel : submitLabel}
        </button>

        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="font-semibold text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onResend}
            disabled={loading}
            className={`${authLinkClass} disabled:opacity-50`}
          >
            Resend OTP
          </button>
        </div>
      </form>
    </div>
  </div>
);

const CheckIcon = () => (
  <span className="w-[23px] h-[23px] mt-0.5 text-cyan-500 dark:text-cyan-400" aria-hidden="true">
    <svg
      viewBox="0 0 24 24"
      className="block w-full h-full fill-none stroke-current"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9.2"></circle>
      <path d="m7.8 12.2 2.8 2.8 5.8-6"></path>
    </svg>
  </span>
);

const BENEFITS = [
  {
    title: "Research Chat.",
    text: "Ask your market questions and get structured answers.",
  },
  {
    title: "Curated Market Intelligence.",
    text: "Insights are synthesized from structured research, not scraped from the open web, so outputs are relevant, consistent, and decision-ready.",
  },
];

const DELIVERABLES = [
  "Market Sizing & Forecasts",
  "Competitive Landscape",
  "Market Segmentation",
  "Opportunity Mapping",
];

const navLinkClass =
  "text-xs sm:text-[15px] font-semibold text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300 transition-colors";

const AuthLayout = ({ title, subtitle, headerLink, cardFooter, children }) => {
  const { isDarkMode, toggleTheme } = useTheme();

  return (
    <div className="relative min-h-screen overflow-hidden bg-gray-50 text-gray-900 font-['Inter',Arial,Helvetica,sans-serif] antialiased dark:bg-gray-950 dark:text-white">
      {/* Background glow - only for dark mode */}
      <div className="hidden dark:block absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-[calc(100%-32px)] sm:w-[min(700px,calc(100%-40px))] lg:w-[min(1220px,calc(100%-64px))] mx-auto">
        {/* Header */}
        <header className="min-h-[68px] sm:min-h-[78px] flex items-center justify-between gap-6">
          <a
            href={WEBSITE_URL}
            className="text-[22px] sm:text-[25px] font-extrabold tracking-[-1.1px] text-gray-900 dark:text-white"
            aria-label="Infinium home"
          >
            Infinium
          </a>
          <nav className="flex items-center gap-3.5 sm:gap-[26px]" aria-label="Main navigation">
            <Link to={headerLink.to} className={navLinkClass}>
              {headerLink.label}
            </Link>
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2.5 rounded-xl bg-white hover:bg-gray-100 text-gray-700 shadow-lg dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-yellow-400 transition-all"
              title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              aria-label={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDarkMode ? (
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path>
                </svg>
              ) : (
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path>
                </svg>
              )}
            </button>
          </nav>
        </header>

        <main className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.12fr)_minmax(390px,0.88fr)] items-start gap-7 sm:gap-[34px] lg:gap-[clamp(42px,6vw,82px)] pt-6 sm:pt-7 lg:pt-[34px] pb-7">
          {/* Intro */}
          <section aria-labelledby="auth-intro-title">
            <p className="m-0 mb-[13px] sm:mb-[18px] text-[11px] sm:text-[13px] leading-[1.4] font-extrabold tracking-[.9px] sm:tracking-[1.15px] uppercase text-cyan-600 dark:text-cyan-400">
              Human crafted market intelligence
            </p>
            <h1
              id="auth-intro-title"
              className="m-0 max-w-[720px] lg:max-w-[630px] text-[clamp(34px,9.2vw,43px)] sm:text-[clamp(38px,7vw,52px)] lg:text-[clamp(38px,4.15vw,54px)] leading-[1.055] tracking-[-1.7px] sm:tracking-[-2.5px] font-extrabold text-gray-900 dark:text-white"
            >
              Ask a market question. Get curated market intelligence.
            </h1>
            <p className="max-w-[720px] lg:max-w-[610px] mt-4 mb-[23px] sm:mt-5 sm:mb-[27px] text-base sm:text-lg leading-[1.55] sm:leading-[1.62] text-gray-600 dark:text-gray-400">
              Get structured answers on sizing, segmentation, competition, and growth forecasts from the best curated human crafted market intelligence warehouse.
            </p>

            <ul className="grid gap-[15px] sm:gap-[17px] list-none m-0 p-0">
              {BENEFITS.map((benefit) => (
                <li key={benefit.title} className="grid grid-cols-[24px_minmax(0,1fr)] gap-[13px] items-start">
                  <CheckIcon />
                  <p className="m-0 text-sm sm:text-base leading-normal text-gray-700 dark:text-gray-300">
                    <strong className="font-bold text-gray-900 dark:text-white">{benefit.title}</strong> {benefit.text}
                  </p>
                </li>
              ))}
            </ul>

            <div className="mt-6 sm:mt-[29px]">
              <h2 className="m-0 mb-3.5 text-base leading-[1.4] font-extrabold">What you can get</h2>
              <div className="flex flex-wrap gap-2 sm:gap-x-[11px] sm:gap-y-2.5">
                {DELIVERABLES.map((item) => (
                  <span
                    key={item}
                    className="inline-flex items-center min-h-[38px] sm:min-h-[42px] px-2.5 sm:px-3.5 py-2 sm:py-[9px] rounded-xl border border-gray-200 bg-white text-xs sm:text-sm font-semibold text-gray-700 dark:border-gray-800 dark:bg-gray-900/50 dark:text-gray-300"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* Form card */}
          <section
            aria-labelledby="auth-card-title"
            className="w-full px-[18px] pt-[23px] pb-[21px] sm:px-[30px] sm:pt-[30px] sm:pb-[26px] rounded-2xl border border-gray-200 bg-white shadow-xl backdrop-blur-xl dark:bg-gray-900/50 dark:border-gray-800"
          >
            <h2
              id="auth-card-title"
              className="m-0 mb-[5px] text-2xl sm:text-[27px] leading-[1.2] tracking-[-1px] font-extrabold text-gray-900 dark:text-white"
            >
              {title}
            </h2>
            <p className="m-0 mb-5 text-sm sm:text-[15px] leading-normal text-gray-600 dark:text-gray-400">
              {subtitle}
            </p>

            {children}

            {cardFooter && (
              <div className="mt-[19px] pt-[15px] border-t border-gray-200 dark:border-gray-800">
                <p className="m-0 text-xs leading-normal text-gray-600 dark:text-gray-400">{cardFooter}</p>
              </div>
            )}
          </section>
        </main>

        {/* Footer */}
        <footer className="flex flex-col items-start gap-3 pb-5 sm:flex-row sm:items-center sm:justify-between sm:gap-5 pt-2 lg:pt-[13px] sm:pb-6 text-gray-500 dark:text-gray-400">
          <p className="m-0 text-xs leading-normal">
            © {new Date().getFullYear()} Infinium Global Research. All rights reserved.
          </p>
          <nav className="flex gap-[18px] sm:gap-[22px]" aria-label="Legal links">
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className={`text-xs ${authLinkClass}`}>
              Terms
            </a>
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={`text-xs ${authLinkClass}`}>
              Privacy
            </a>
          </nav>
        </footer>
      </div>
    </div>
  );
};

export default AuthLayout;
